import { useState, useRef, useEffect } from "react";
import { ChecklistItems } from "@/bryntum-lib/models/ChecklistItems";
import { useSwivuChecklistItems } from "./useSwivuChecklistItems";

/**
 * Hook to manage drag-and-drop reordering of checklist items
 * Extracted from ChecklistAddItemsModal for reusability
 */
export const useChecklistItemDragDrop = (items: ChecklistItems[], scrollContainerRef?: React.RefObject<HTMLElement>) => {
  const [draggedItem, setDraggedItem] = useState<ChecklistItems | null>(null);
  const [dragOverItem, setDragOverItem] = useState<ChecklistItems | null>(null);
  const { updateChecklistItem } = useSwivuChecklistItems();
  const scrollIntervalRef = useRef<number | null>(null);

  // Clean up scroll interval on unmount or when drag ends
  useEffect(() => {
    return () => {
      if (scrollIntervalRef.current) {
        cancelAnimationFrame(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }
    };
  }, []);

  const handleDragStart = (item: ChecklistItems) => {
    setDraggedItem(item);
  };

  const handleAutoScroll = (clientY: number) => {
    if (!scrollContainerRef?.current) return;

    const container = scrollContainerRef.current;
    const rect = container.getBoundingClientRect();
    const scrollThreshold = 50; // pixels from edge to trigger scroll
    const scrollSpeed = 5; // pixels per frame

    const distanceFromTop = clientY - rect.top;
    const distanceFromBottom = rect.bottom - clientY;

    const scroll = () => {
      if (!scrollContainerRef?.current) return;

      if (distanceFromTop < scrollThreshold && distanceFromTop > 0) {
        // Scroll up
        container.scrollTop = Math.max(0, container.scrollTop - scrollSpeed);
        scrollIntervalRef.current = requestAnimationFrame(scroll);
      } else if (distanceFromBottom < scrollThreshold && distanceFromBottom > 0) {
        // Scroll down
        const maxScroll = container.scrollHeight - container.clientHeight;
        container.scrollTop = Math.min(maxScroll, container.scrollTop + scrollSpeed);
        scrollIntervalRef.current = requestAnimationFrame(scroll);
      } else {
        // Stop scrolling
        if (scrollIntervalRef.current) {
          cancelAnimationFrame(scrollIntervalRef.current);
          scrollIntervalRef.current = null;
        }
      }
    };

    // Cancel any existing scroll animation
    if (scrollIntervalRef.current) {
      cancelAnimationFrame(scrollIntervalRef.current);
    }

    // Start scrolling if needed
    if ((distanceFromTop < scrollThreshold && distanceFromTop > 0) ||
        (distanceFromBottom < scrollThreshold && distanceFromBottom > 0)) {
      scrollIntervalRef.current = requestAnimationFrame(scroll);
    }
  };

  const handleDragOver = (e: React.DragEvent, item: ChecklistItems) => {
    e.preventDefault();
    if (draggedItem && draggedItem.id !== item.id) {
      setDragOverItem(item);
    }

    // Handle auto-scroll
    handleAutoScroll(e.clientY);
  };

  const handleDragEnd = () => {
    // Stop any ongoing scroll animation
    if (scrollIntervalRef.current) {
      cancelAnimationFrame(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }

    if (draggedItem && dragOverItem && draggedItem.id !== dragOverItem.id) {
      const draggedIndex = items.findIndex((item) => item.id === draggedItem.id);
      const targetIndex = items.findIndex((item) => item.id === dragOverItem.id);

      // Reorder items
      const reorderedItems = [...items];
      const [removed] = reorderedItems.splice(draggedIndex, 1);
      reorderedItems.splice(targetIndex, 0, removed);

      // Update order values
      reorderedItems.forEach((item, index) => {
        updateChecklistItem(item, { order: index + 1 });
      });
    }
    setDraggedItem(null);
    setDragOverItem(null);
  };

  return {
    draggedItem,
    dragOverItem,
    handleDragStart,
    handleDragOver,
    handleDragEnd
  };
};
